import React, { forwardRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '../../../../../../../../../../app/hooks'
import {
  AttachmentDataWithRequiredAttachment,
  AttachmentStatus,
  AttachmentType,
} from '../../../../../../../../../../@types/types'
import { useFeatureSwitch } from '../../../../../../../../../../utils/features/FeatureSwitchContext'
import { FeatureSwitch } from '../../../../../../../../../../utils/features/featureSwitches'
import { isWebView } from '../../../../../../../../../../utils/navigation'
import { WebViewMultimediaPreview } from './WebViewMultimediaPreview'
import { InitialUploadClickableArea } from './InitialUploadClickableArea'
import styles from './MobileMultimediaPreview.module.css'

interface MobileMultimediaButtonProps {
  dropzoneId: string
}

export const WebViewMultimediaButton = forwardRef<HTMLDivElement, MobileMultimediaButtonProps>(
  ({ dropzoneId }, ref) => {
    const { t } = useTranslation()
    const attachments = useAppSelector((state) => state.multimediaFeature.attachments) ?? []
    const isMobileFlow = useAppSelector((state) => state.flowFeature.flowInformation.isMobileFlow)
    const { isActive: isInlinePicturesEnabled } = useFeatureSwitch(
      FeatureSwitch.PPA_13285_RENT_SALE_MOBILE_NEW_IMAGE_UPLOAD,
      isWebView() && isMobileFlow
    )
    const [deletedPictureIds, setDeletedPictureIds] = useState<string[]>([])
    const pictures = attachments.filter(
      (item): item is AttachmentDataWithRequiredAttachment =>
        item.attachmentType === AttachmentType.PICTURE &&
        item.state === AttachmentStatus.PERSISTED &&
        !!item.attachment?.url &&
        !deletedPictureIds.includes(item.attachment.id)
    )
    const hasPictures = pictures.length > 0

    return (
      <div id={dropzoneId}>
        <div ref={ref} style={{ display: 'none' }} />
        {hasPictures ? (
          <WebViewMultimediaPreview
            dropzoneId={`${dropzoneId}-add`}
            pictures={pictures}
            isInlinePicturesEnabled={isInlinePicturesEnabled}
            onPictureDeleted={(pictureId) => setDeletedPictureIds((ids) => [...ids, pictureId])}
          />
        ) : (
          <InitialUploadClickableArea dropzoneId={`${dropzoneId}-empty`} />
        )}
        {isInlinePicturesEnabled && (
          <p className={`${styles.tip} font-body-small-regular`}>
            {t('wizard.screen.multimedia.webview.inlinePictures.uploadTip')}
          </p>
        )}
      </div>
    )
  }
)
